import { useLazyQuery } from '@apollo/client';
import { Empty, Select } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { LIMIT, SORT } from '../../../../../common/constants';
import SelectComponent from '../../../../../components/SelectComponent';
import { LIBRARIES } from '../../../../raiseRequest/graphql/Queries';

const { Option } = Select;

const LibrarySelect = (props) => {
  const { isModalOpen = true, ...rest } = props;
  const [libraryList, setLibraryList] = useState([]);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [isAllLibrariesFetched, setIsAllLibrariesFetched] = useState(false);
  const [isLibrariesAllowClear, setIsLibrariesAllowClear] = useState(false);
  const [isLibrariesDropdownVisible, setIsLibrariesDropdownVisible] = useState(
    false,
  );
  const [
    isFetchMoreLibrariesLoading,
    setIsFetchMoreLibrariesLoading,
  ] = useState(false);

  const [libraries, { loading: isLibraryLoading }] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      if (response?.libraries?.data?.length < LIMIT) {
        setIsAllLibrariesFetched(true);
      }
      setIsFetchMoreLibrariesLoading(false);

      setLibraryList([...libraryList, ...response?.libraries?.data]);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    setLibrarySearchTerm('');
    setLibraryList([]);
    if (isModalOpen) {
      libraries({
        variables: {
          filter: {
            limit: LIMIT,
            skip: 0,
            search: librarySearchTerm,
          },
          sort: {
            field: 'createdAt',
            order: SORT?.DESC,
          },
        },
      });
    }
    setIsAllLibrariesFetched(false);
  }, [isLibrariesDropdownVisible, isModalOpen]);

  const handleLibrariesScroll = (event) => {
    if (!isAllLibrariesFetched && !isLibraryLoading) {
      const target = event?.target;
      const currentLength = libraryList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        setIsFetchMoreLibrariesLoading(true);
        libraries({
          variables: {
            filter: {
              limit: LIMIT,
              skip: currentLength,
              search: librarySearchTerm,
            },
            sort: {
              field: 'createdAt',
              order: SORT?.DESC,
            },
          },
        });
      }
    }
  };

  const handleLibrariesSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();
    setIsAllLibrariesFetched(false);
    setLibraryList([]);
    setLibrarySearchTerm(trimSearch);
    libraries({
      variables: {
        filter: {
          limit: LIMIT,
          skip: 0,
          search: trimSearch,
        },
        sort: {
          field: 'createdAt',
          order: SORT?.DESC,
        },
      },
    });
  };

  const handleLibrariesScrollDebounce = debounce(handleLibrariesScroll, 500);
  const handleSearchLibrariesDebounce = debounce(handleLibrariesSearch, 500);

  useEffect(() => {
    handleLibrariesScrollDebounce?.cancel();
  }, [handleLibrariesScrollDebounce]);

  useEffect(() => {
    handleSearchLibrariesDebounce?.cancel();
  }, [handleSearchLibrariesDebounce]);

  useEffect(() => {
    if (isLibraryLoading) {
      setIsLibrariesAllowClear(false);
    } else {
      setIsLibrariesAllowClear(true);
    }
  }, [isLibraryLoading]);

  return (
    <SelectComponent
      showSearch
      multiple
      loading={
        (isLibraryLoading && isLibrariesDropdownVisible) ||
        isFetchMoreLibrariesLoading
      }
      mode="multiple"
      placeholder="Select Library"
      optionFilterProp="children"
      allowClear={isLibrariesAllowClear}
      onPopupScroll={handleLibrariesScrollDebounce}
      onSearch={handleSearchLibrariesDebounce}
      onDropdownVisibleChange={(visible) =>
        setIsLibrariesDropdownVisible(visible)
      }
      filterOption={false}
      notFoundContent={
        !isFetchMoreLibrariesLoading && isLibraryLoading ? (
          <span>Loading...</span>
        ) : (
          <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      {...rest}
    >
      {libraryList?.map((lib) => (
        <Option key={lib?.id} value={lib?.id}>
          {lib?.name} {lib?.libraryNumber ? `(${lib.libraryNumber})` : ''}
        </Option>
      ))}
    </SelectComponent>
  );
};

export default LibrarySelect;
